import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";

// Current document ID — defaults to "default" for backward compat
let currentDocId = "default";

export let ydoc = new Y.Doc();
export let yText = ydoc.getText("script");
export let yFeatures = ydoc.getArray("featureTree");

// ---------------------------------------------------------------------------
// y-websocket provider for cross-device sync via server relay.
// Falls back to BroadcastChannel for local dev without a server.
// ---------------------------------------------------------------------------

let wsProvider: WebsocketProvider | null = null;
let channel: BroadcastChannel | null = null;

export function getAwareness() {
  return wsProvider?.awareness ?? null;
}

function getWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

function initSync(docId: string) {
  // Try y-websocket first (works when server is running)
  try {
    wsProvider = new WebsocketProvider(getWsUrl(), docId, ydoc, {
      connect: true,
      // Reconnect with exponential backoff
      maxBackoffTime: 10000,
    });
    wsProvider.on("status", ({ status }: { status: string }) => {
      if (status === "connected") {
        console.log(`Yjs: connected to server (doc: ${docId})`);
      }
    });
  } catch {
    // Server not available — fall through to BroadcastChannel
  }

  // Always enable BroadcastChannel for same-device tab sync
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(`cad-plm-sync-${docId}`);
    ydoc.on("update", (update: Uint8Array, origin: unknown) => {
      if (origin === "broadcast-channel") return;
      channel?.postMessage(update);
    });
    channel.onmessage = (evt: MessageEvent) => {
      Y.applyUpdate(ydoc, new Uint8Array(evt.data), "broadcast-channel");
    };
  }
}

initSync(currentDocId);

/**
 * Reinitialize the Y.Doc and providers for a different document.
 * Call this when the user opens a different document.
 */
export function setDocId(id: string) {
  if (id === currentDocId) return;

  // Tear down existing providers
  wsProvider?.destroy();
  wsProvider = null;
  channel?.close();
  channel = null;
  ydoc.destroy();

  // Create fresh doc + providers
  currentDocId = id;
  ydoc = new Y.Doc();
  yText = ydoc.getText("script");
  yFeatures = ydoc.getArray("featureTree");
  initSync(id);
}

export function destroy() {
  wsProvider?.destroy();
  wsProvider = null;
  channel?.close();
  channel = null;
  ydoc.destroy();
}
